import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import { exportToExcel } from "../utils/exportData";
import { BASE_URL } from "../api";
import { getApiHeaders, formatDateDDMMYYYY } from "../utils/helpers";
import axios from "axios";
import CommonTable from "../component/CommonTable";
import ExportButton from "../component/ExportButton";
import MobileFilter from "../component/MobileFilter";

export default function ClaimHistory() {
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    const fetchClaimHistory = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/deposit/bonus_transfer_list`, {
          headers: getApiHeaders(),
        });

        const raw =
          res.data?.data ??
          res.data?.bonus_transfer_list ??
          res.data?.transfer_list ??
          res.data?.claim_list ??
          res.data?.history ??
          (Array.isArray(res.data) ? res.data : []);

        let list = [];
        if (Array.isArray(raw)) {
          list = Array.isArray(raw[0]) ? raw.flat(Infinity) : raw;
        } else if (raw && typeof raw === "object") {
          list = [raw];
        }

        const validRecords = list.filter((item) => {
          if (!item || typeof item !== "object") return false;
          const keys = Object.keys(item);
          const nonMsgKeys = keys.filter(
            (k) => !["msg", "msgcode", "Msg", "MsgCode", "message"].includes(k)
          );
          return nonMsgKeys.length > 0;
        });

        setData(validRecords);
      } catch (error) {
        console.error("Error fetching claim history:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClaimHistory();
  }, []);

  const getItemDateString = (item) => {
    const dateVal = item.ondate || item.created_at || item.date || item.datetime;
    if (!dateVal) return "";
    if (typeof dateVal === "string") {
      if (dateVal.includes("T")) return dateVal.split("T")[0];
      if (dateVal.includes(" ")) return dateVal.split(" ")[0];
    }
    const d = new Date(dateVal);
    if (!isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return "";
  };

  const filteredData = data.filter((item) => {
    let match = true;

    if (appliedFromDate || appliedToDate) {
      const itemDateStr = getItemDateString(item);
      if (itemDateStr) {
        const itemDate = new Date(itemDateStr);
        itemDate.setHours(0, 0, 0, 0);

        if (appliedFromDate) {
          const from = new Date(appliedFromDate);
          from.setHours(0, 0, 0, 0);
          if (itemDate < from) match = false;
        }

        if (appliedToDate) {
          const to = new Date(appliedToDate);
          to.setHours(23, 59, 59, 999);
          if (itemDate > to) match = false;
        }
      }
    }

    if (!match) return false;

    if (appliedSearchTerm) {
      if (!item || typeof item !== "object") return false;
      match = Object.values(item).some((val) =>
        String(val ?? "")
          .toLowerCase()
          .includes(appliedSearchTerm.toLowerCase())
      );
    }

    return match;
  });

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm);
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setAppliedSearchTerm("");
    setAppliedFromDate("");
    setAppliedToDate("");
  };

  const handleExport = () => {
    const exportData = filteredData.map((item, index) => ({
      "#": index + 1,
      Date: formatDateDDMMYYYY(item.ondate || item.created_at || item.date) || "-",
      "Invoice ID": item.invoiceid || item.id || "-",
      "Transfer Amount": item.transfer_amount || item.amount || 0,
      Remarks: item.remarks || "-",
      Status: item.status || "Completed",
    }));
    exportToExcel(exportData, "Bonus_Claim_History", "default");
  };

  const columns = [
    {
      header: "Date",
      render: (row) => (
        <span>
          {formatDateDDMMYYYY(row.ondate || row.created_at || row.date) || "-"}
        </span>
      ),
    },
    {
      header: "Invoice ID",
      render: (row) => (
        <span className="font-monospace font-size-12 fw-semibold">
          {row.invoiceid || row.id || "-"}
        </span>
      ),
    },
    {
      header: "Transfer Amount",
      render: (row) => {
        const amt = row.transfer_amount ?? row.amount ?? row.bonus_amount;
        return (
          <span className="fw-bold" style={{ color: "var(--bs-primary)" }}>
            {amt != null ? `$${parseFloat(amt).toFixed(2)}` : "-"}
          </span>
        );
      },
    },
    {
      header: "Remarks",
      render: (row) => (
        <span className="font-size-13" style={{ color: "var(--bs-secondary-color)" }}>
          {row.remarks || "-"}
        </span>
      ),
    },
    {
      header: "Status",
      render: (row) => {
        const rawStatus = row.status || row.istatus || "Completed";
        const statusStr = String(rawStatus).toLowerCase();
        const isSuccess = ["completed", "approved", "done", "credited", "claimed", "success", "1"].includes(statusStr);
        const isWarning = ["pending", "0", "review", "processing"].includes(statusStr);

        return (
          <span
            className={`badge fw-bold px-3 py-2 rounded-pill ${
              isSuccess
                ? "bg-success bg-opacity-10 text-success"
                : isWarning
                ? "bg-warning bg-opacity-10 text-warning"
                : "bg-danger bg-opacity-10 text-danger"
            }`}
          >
            {rawStatus}
          </span>
        );
      },
    },
  ];

  return (
    <div id="layout-wrapper">
      <Topbar />
      <Sidebar />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid mb-4">
            {/* Standard Page Title & Buttons matching other pages */}
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <div>
                      <h2 className="fw-700 mb-0">Claim History</h2>
                      <p
                        className="mb-0 font-size-13"
                        style={{ color: "var(--bs-secondary-color)" }}
                      >
                        View your transferred and claimed bonus history
                      </p>
                    </div>
                  </div>

                  <div className="page-title-right d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                      onClick={() => navigate("/bonus_history")}
                    >
                      <i className="bx bx-history me-2 fs-5"></i> Bonus History
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                      onClick={() => navigate("/bonus-programs")}
                    >
                      <i className="bx bx-gift me-2 fs-5"></i> Bonus Programs
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Table Card */}
            <div className="row">
              <div className="col-12">
                <div className="card shadow-sm border-0" style={{ borderRadius: "12px" }}>
                  <div className="card-body">
                    {/* Desktop Filters */}
                    <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                      <div>
                        <label htmlFor="fromDate">From Date</label>
                        <input
                          className="form-control"
                          type="date"
                          name="fromDate"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          max={toDate || today}
                          id="fromDate"
                        />
                      </div>
                      <div>
                        <label htmlFor="toDate">To Date</label>
                        <input
                          className="form-control"
                          type="date"
                          name="toDate"
                          min={fromDate || undefined}
                          max={today}
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          id="toDate"
                        />
                      </div>
                      <div>
                        <label htmlFor="search">Search</label>
                        <input
                          className="form-control"
                          type="text"
                          id="search"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-primary waves-effect waves-light"
                          onClick={handleSearch}
                        >
                          Search
                        </button>
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-light border waves-effect waves-light"
                          onClick={clearFilters}
                          disabled={
                            !fromDate &&
                            !toDate &&
                            !searchTerm &&
                            !appliedFromDate &&
                            !appliedToDate &&
                            !appliedSearchTerm
                          }
                        >
                          Clear
                        </button>
                      </div>
                      <div className="ms-auto">
                        <ExportButton
                          disabled={!filteredData || filteredData.length === 0}
                          onClick={handleExport}
                        />
                      </div>
                    </div>

                    {/* Mobile Filters */}
                    <MobileFilter
                      title="Claim History"
                      onExport={handleExport}
                      exportDisabled={!filteredData || filteredData.length === 0}
                    >
                      <div>
                        <label htmlFor="fromDateMobile">From Date</label>
                        <input
                          className="form-control"
                          type="date"
                          name="fromDate"
                          value={fromDate}
                          onChange={(e) => setFromDate(e.target.value)}
                          max={toDate || today}
                          id="fromDateMobile"
                        />
                      </div>
                      <div>
                        <label htmlFor="toDateMobile">To Date</label>
                        <input
                          className="form-control"
                          type="date"
                          name="toDate"
                          min={fromDate || undefined}
                          max={today}
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          id="toDateMobile"
                        />
                      </div>
                      <div>
                        <label htmlFor="searchMobile">Search</label>
                        <input
                          className="form-control"
                          type="text"
                          id="searchMobile"
                          placeholder="Search..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-primary waves-effect waves-light flex-grow-1"
                          onClick={handleSearch}
                          data-bs-dismiss="offcanvas"
                          data-bs-target="#filterOffcanvasMobile"
                        >
                          Search
                        </button>
                        <button
                          type="button"
                          className="btn btn-light waves-effect waves-light border flex-grow-1"
                          onClick={clearFilters}
                          disabled={
                            !fromDate &&
                            !toDate &&
                            !searchTerm &&
                            !appliedFromDate &&
                            !appliedToDate &&
                            !appliedSearchTerm
                          }
                          data-bs-dismiss="offcanvas"
                          data-bs-target="#filterOffcanvasMobile"
                        >
                          Clear
                        </button>
                      </div>
                    </MobileFilter>

                    {/* Standard CommonTable */}
                    <CommonTable
                      key={`${appliedFromDate}_${appliedToDate}`}
                      columns={columns}
                      data={filteredData}
                      loading={loading}
                      loadingMessage={
                        <div>
                          <div
                            className="spinner-border text-primary spinner-border-sm me-2"
                            role="status"
                          ></div>
                          <span>Loading claim history...</span>
                        </div>
                      }
                      noDataMessage="No claim history records found"
                      tableClassName="table table-hover align-middle table-nowrap mb-0"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
